import type { ICommand } from '../../commands/ICommand';
import { useScenariosStore } from '../../assistant/stores/scenariosStore';
import {
  type CommandDeltaContribution,
  isScenarioRecordable,
} from './ScenarioRecordable';

export class ScenarioAwareCommand implements ICommand {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly timestamp: number;

  private innerCommand: ICommand;
  private executedInScenario: boolean = false;
  private deltaContribution: CommandDeltaContribution | null = null;

  constructor(innerCommand: ICommand) {
    this.innerCommand = innerCommand;
    this.id = `scenario-aware-${innerCommand.id}`;
    this.name = innerCommand.name;
    this.description = innerCommand.description;
    this.timestamp = innerCommand.timestamp;
  }

  getInnerCommand(): ICommand {
    return this.innerCommand;
  }

  wasExecutedInScenario(): boolean {
    return this.executedInScenario;
  }

  canExecute(): boolean {
    return this.innerCommand.canExecute?.() ?? true;
  }

  canUndo(): boolean {
    return this.innerCommand.canUndo?.() ?? true;
  }

  canRedo(): boolean {
    return this.innerCommand.canRedo?.() ?? true;
  }

  async execute(): Promise<void> {
    const store = useScenariosStore.getState();
    const { scenarioState, activeScenarioId } = store;

    if (scenarioState === 'EDITING_SCENARIO' && activeScenarioId) {
      if (isScenarioRecordable(this.innerCommand)) {
        this.deltaContribution = this.innerCommand.toScenarioDelta();
        this.executedInScenario = true;
        if (this.deltaContribution.edgeAdditions?.some((edge) => edge.type === 'matrix_reports')) {
          console.info('[MatrixScenarioDebug] matrix delta accepted by scenario wrapper', {
            commandId: this.innerCommand.id,
            commandName: this.innerCommand.name,
            scenarioState,
            activeScenarioId,
            contribution: this.deltaContribution,
          });
        }
        // Recording replaces execution, so an empty contribution silently
        // discards the user's edit — the command reports success, the scenario
        // shows no pending change, and nothing is written. That is almost
        // always a `toScenarioDelta()` that does not cover every field its
        // `execute()` handles, which is invisible without this.
        if (isEmptyDeltaContribution(this.deltaContribution)) {
          console.warn(
            `[ScenarioAwareCommand] "${this.innerCommand.name}" recorded an empty scenario delta; `
              + 'the edit was dropped. Check that toScenarioDelta() covers every field execute() writes.'
          );
        }
        this.applyDeltaToScenario(this.deltaContribution);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[ScenarioAwareCommand] Command "${this.innerCommand.name}" does not implement IScenarioRecordable. ` +
            'It will execute normally even in scenario editing mode.'
        );
      }
    }

    this.executedInScenario = false;
    await this.innerCommand.execute();
  }

  async undo(): Promise<void> {
    if (this.executedInScenario && this.deltaContribution) {
      this.removeDeltaFromScenario(this.deltaContribution);
      return;
    }

    await this.innerCommand.undo?.();
  }

  async redo(): Promise<void> {
    const store = useScenariosStore.getState();
    const { scenarioState, activeScenarioId } = store;

    if (
      this.executedInScenario &&
      scenarioState === 'EDITING_SCENARIO' &&
      activeScenarioId &&
      this.deltaContribution
    ) {
      this.applyDeltaToScenario(this.deltaContribution);
      return;
    }

    await this.innerCommand.redo?.();
  }

  private applyDeltaToScenario(contribution: CommandDeltaContribution): void {
    const store = useScenariosStore.getState();
    const result = store.applyDeltaContributionToActiveScenario(contribution);
    if (contribution.edgeAdditions?.some((edge) => edge.type === 'matrix_reports')) {
      const activeScenario = useScenariosStore.getState().getActiveScenario();
      console.info('[MatrixScenarioDebug] matrix delta apply result', {
        success: result.success,
        error: result.success ? undefined : result.error,
        activeScenarioId: useScenariosStore.getState().activeScenarioId,
        edgeAdditions: activeScenario?.delta.edgeAdditions,
      });
    }
    if (!result.success && process.env.NODE_ENV === 'development') {
      console.warn('[ScenarioAwareCommand] Failed to apply delta contribution:', result.error);
    }
  }

  private removeDeltaFromScenario(contribution: CommandDeltaContribution): void {
    const store = useScenariosStore.getState();
    const result = store.removeDeltaContributionFromActiveScenario(contribution);
    if (!result.success && process.env.NODE_ENV === 'development') {
      console.warn('[ScenarioAwareCommand] Failed to remove delta contribution:', result.error);
    }
  }

  canMergeWith(other: ICommand): boolean {
    return this.innerCommand.canMergeWith?.(other) ?? false;
  }

  mergeWith(other: ICommand): ICommand {
    if (!this.innerCommand.mergeWith) {
      throw new Error('Merging not supported for this command');
    }
    const merged = this.innerCommand.mergeWith(other);
    return new ScenarioAwareCommand(merged);
  }

  getMetadata(): Record<string, unknown> {
    return {
      ...this.innerCommand.getMetadata?.(),
      scenarioAware: true,
      executedInScenario: this.executedInScenario,
    };
  }

  toString(): string {
    return `ScenarioAware(${this.innerCommand.toString?.() ?? this.innerCommand.name})`;
  }
}

function isEmptyDeltaContribution(contribution: CommandDeltaContribution): boolean {
  return !(
    contribution.additions?.length
    || contribution.removals?.length
    || contribution.moves?.length
    || contribution.updates?.length
    || contribution.edgeAdditions?.length
    || contribution.edgeRemovals?.length
  );
}

export function wrapWithScenarioAwareness(command: ICommand): ICommand {
  if (isScenarioRecordable(command)) {
    return new ScenarioAwareCommand(command);
  }
  return command;
}

export function isScenarioEditingActive(): boolean {
  const { scenarioState, activeScenarioId } = useScenariosStore.getState();
  return scenarioState === 'EDITING_SCENARIO' && activeScenarioId !== null;
}
